import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Imperative marker clustering (leaflet.markercluster) for the trail detail map.
// Points are clustered with a count badge that splits apart on zoom. Routes stay
// as React <Polyline>s; the attraction "Show route" button is wired via popup DOM.
export default function MarkerClusterLayer({ points, layerMeta, routeForAttraction, shownRouteIdsRef, onToggleRoute, onSelectPoint }) {
  const map = useMap();
  const ref = useRef({});
  ref.current = { routeForAttraction, shownRouteIdsRef, onToggleRoute, layerMeta, onSelectPoint };

  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 50,
      chunkedLoading: true,
      iconCreateFunction: (cluster) => {
        const n = cluster.getChildCount();
        const size = n < 10 ? 34 : n < 50 ? 40 : 46;
        return L.divIcon({
          className: "rider-cluster",
          html: `<div data-testid="rider-map-cluster" style="display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:9999px;background:rgba(14,165,233,.92);color:#fff;font-weight:700;font-size:13px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)">${n}</div>`,
          iconSize: [size, size],
        });
      },
    });

    const buildPopup = (p, meta) => {
      const maps = `https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`;
      let links = "";
      if (p.website) links += `<a href="${esc(p.website)}" target="_blank" rel="noreferrer" style="color:#0284c7;font-size:12px;margin-right:10px">Site</a>`;
      if (p.phone) links += `<a href="tel:${esc(p.phone)}" style="color:#475569;font-size:12px;margin-right:10px">Call</a>`;
      links += `<a href="${maps}" target="_blank" rel="noreferrer" style="color:#059669;font-size:12px">Go</a>`;
      let routeBtn = "";
      const rr = p._t === "attraction" ? ref.current.routeForAttraction(p.id) : null;
      if (rr) {
        const on = ref.current.shownRouteIdsRef.current.has(rr.id);
        const col = rr.color || "#22D3EE";
        routeBtn = `<button data-rid="${esc(rr.id)}" data-rcolor="${esc(col)}" data-testid="rider-map-showroute-${esc(p.id)}" style="margin-top:6px;width:100%;font-size:12px;font-weight:600;border-radius:4px;padding:4px 8px;cursor:pointer;${on ? `background:${col};color:#fff;border:none` : `background:#fff;color:${col};border:1px solid ${col}`}">${on ? "Hide route" : "Show route"}</button>`;
      }
      return `<div data-testid="rider-map-popup-${esc(p.id)}" style="min-width:170px">
        <div style="font-weight:700;font-size:14px;color:${meta.color}">${esc(p.name || meta.label)}</div>
        ${p.category ? `<div style="font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#64748b">${esc(p.category)}</div>` : ""}
        ${p.short_description ? `<p style="font-size:12px;color:#475569;margin:4px 0">${esc(p.short_description)}</p>` : ""}
        ${p.notes ? `<p style="font-size:12px;color:#475569;margin:4px 0">${esc(p.notes)}</p>` : ""}
        <div style="margin-top:4px">${links}</div>
        ${routeBtn}
      </div>`;
    };

    const BIZ_TYPES = new Set(["advertiser", "attraction", "bike_shop"]);

    points.forEach((p) => {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
      const meta = layerMeta[p._t] || { color: "#64748b", label: "" };
      const icon = L.divIcon({
        className: "rider-marker",
        html: `<span style="display:block;width:16px;height:16px;border-radius:9999px;background:${meta.color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></span>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
        popupAnchor: [0, -8],
      });
      const m = L.marker([p.lat, p.lng], { icon });
      m.bindTooltip(esc(p.name || meta.label));
      if (BIZ_TYPES.has(p._t)) {
        // Business/attraction/shop: tap opens the shared rich detail card.
        m.on("click", () => ref.current.onSelectPoint && ref.current.onSelectPoint(p));
      } else {
        // Amenity (bathroom/parking/trailhead/water): keep the lightweight popup.
        m.bindPopup(() => buildPopup(p, meta));
        m.on("popupopen", (e) => {
          const el = e.popup.getElement();
          const btn = el && el.querySelector("[data-rid]");
          if (!btn) return;
          btn.onclick = () => {
            const rid = btn.getAttribute("data-rid");
            const col = btn.getAttribute("data-rcolor") || "#22D3EE";
            const wasOn = ref.current.shownRouteIdsRef.current.has(rid);
            ref.current.onToggleRoute(rid);
            const nowOn = !wasOn;
            btn.textContent = nowOn ? "Hide route" : "Show route";
            btn.style.cssText = `margin-top:6px;width:100%;font-size:12px;font-weight:600;border-radius:4px;padding:4px 8px;cursor:pointer;${nowOn ? `background:${col};color:#fff;border:none` : `background:#fff;color:${col};border:1px solid ${col}`}`;
          };
        });
      }
      group.addLayer(m);
    });

    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [points, map, layerMeta]);

  return null;
}
